import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "dnc.install.dismissed";

/**
 * Discreet PWA install prompt. Listens for the `beforeinstallprompt` event
 * (Chrome/Edge/Android) and surfaces a small pill in the bottom-left corner.
 * iOS Safari doesn't fire this event — a light "Add to Home Screen" hint is
 * shown for iOS on first visit only.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY)) return;

    // Detect iOS Safari (no beforeinstallprompt support).
    const ua = window.navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const inStandalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    setIsIOS(iOS);
    if (iOS && !inStandalone) {
      // Show the hint once after a short delay so it doesn't fight the hero.
      const t = setTimeout(() => setShowIOSHint(true), 4000);
      return () => clearTimeout(t);
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      localStorage.setItem(DISMISS_KEY, "installed");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setVisible(false);
    setDeferred(null);
    if (outcome === "dismissed") localStorage.setItem(DISMISS_KEY, "dismissed");
  };

  const dismiss = () => {
    setVisible(false);
    setShowIOSHint(false);
    localStorage.setItem(DISMISS_KEY, "dismissed");
  };

  if (visible) {
    return (
      <div
        data-testid="pwa-install-prompt"
        className="fixed bottom-6 left-6 z-40 flex items-center gap-3 border border-zinc-950 bg-zinc-950 px-4 py-3 text-white shadow-2xl"
      >
        <Download className="h-4 w-4 text-rose-400" />
        <span className="font-mono text-[10px] uppercase tracking-[0.25em]">Install Daily Capsule</span>
        <button
          type="button"
          data-testid="pwa-install-btn"
          onClick={install}
          className="bg-rose-600 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-white hover:bg-white hover:text-zinc-950 transition-colors"
        >
          Install
        </button>
        <button
          type="button"
          data-testid="pwa-dismiss-btn"
          onClick={dismiss}
          aria-label="Dismiss"
          className="ml-1 text-zinc-500 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (isIOS && showIOSHint) {
    return (
      <div
        data-testid="pwa-ios-hint"
        className="fixed bottom-6 left-6 right-6 z-40 flex items-start gap-3 border border-zinc-950 bg-white px-4 py-3 shadow-2xl md:right-auto md:max-w-sm"
      >
        <Download className="mt-0.5 h-4 w-4 text-rose-600" />
        <div className="flex-1 text-xs leading-relaxed text-zinc-700">
          Add to Home Screen: tap <span className="font-semibold">Share</span> →
          <span className="font-semibold"> Add to Home Screen</span>.
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-zinc-500 hover:text-zinc-950"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return null;
}
